import { FlagPurposeType } from './Flags';
import { FinanceType } from './ProductPricing';

export enum ChecklistEvaluationKey {
  systemSize = 'systemSize',
  firstYearProduction = 'firstYearProduction',
  arrayPitch = 'arrayPitch',
  serialValidation = 'serialValidation',
  solarEdgeOptimizerValidation = 'solarEdgeOptimizerValidation',
  systemLoss = 'systemLoss',
}

export type EvaluationVerdict = 'pass' | 'fail' | 'incomplete';

// Signed fractional bounds, e.g. { minPct: -0.05, maxPct: 0.10 } = -5%/+10%.
export interface ToleranceBound {
  minPct?: number;
  maxPct?: number;
}

// Template-authored params. Each descriptor reads only the fields it needs.
export interface EvaluationParams {
  tolerancesByFinanceType?: Partial<Record<FinanceType | 'default', ToleranceBound>>;
  toleranceDegrees?: number;
  minDegrees?: number;
  maxDegrees?: number;
  /** Selects which entry of a keyed reference (e.g. Annex II by OEM) a descriptor matches against. */
  oem?: string;
}

// ---- Persisted state (item.evaluations[inputKey]) ----
export interface EvaluationRowState {
  entered: Record<string, number | null>;
  verdict: EvaluationVerdict;
}
export interface EvaluationState {
  key: ChecklistEvaluationKey;
  rows: EvaluationRowState[];
  verdict: EvaluationVerdict;
  /** Snapshot of the derived rows at the time entries were last saved. Used to detect when
   * design data changes after the reviewer has already recorded measurements. */
  derivedSignature?: string;
}

// ---- Per-row compute output (pure) ----
export interface EvaluationRowResult {
  verdict: EvaluationVerdict;
  percentDifference?: number;
  difference?: number;
  lowerBound?: number;
  upperBound?: number;
  qualificationType?: 'full' | 'nonPfeOnly';
}

// ---- View (response-only; derived + computed, never persisted) ----
export interface EvaluationRowView extends EvaluationRowState {
  derived: Record<string, number | undefined>;
  result: EvaluationRowResult;
}
export interface EvaluationView {
  key: ChecklistEvaluationKey;
  params: EvaluationParams;
  rows: EvaluationRowView[];
  verdict: EvaluationVerdict;
  /** False when the design data the evaluation depends on has not been populated. The
   * evaluation cannot be completed while this is false. Absent means true. */
  hasRequiredData?: boolean;
}

export interface MetricFieldFormat {
  label: string;
  unit?: string;
  decimals?: number;
}

// ---- Core descriptor (pure; lives in types) ----
export interface EvaluationCoreDescriptor {
  key: ChecklistEvaluationKey;
  label: string;
  repeatable: boolean;
  supportsDecisionOptions: boolean;
  defaultParams: EvaluationParams;
  flagPurposeType: FlagPurposeType;
  computeRow(
    derived: Record<string, number | undefined>,
    entered: Record<string, number | null>,
    params: EvaluationParams,
  ): EvaluationRowResult;
  buildFlagReason(rows: EvaluationRowView[], params: EvaluationParams): string;
  /**
   * PDF-snapshot label/unit/decimals for every field this evaluation puts in `derived` or `entered`,
   * including derived-only reference values `computeRow` never reads (the snapshot renders a metric
   * row per populated field, not per field `computeRow` consumes). A field with no entry falls back
   * to a humanized form of its own literal name.
   */
  fieldFormats?: Record<string, MetricFieldFormat>;
  /** Verdict-specific PDF-snapshot messaging. A verdict with no entry falls back to a generic tolerance-check message. */
  verdictMessages?: Partial<Record<EvaluationVerdict, string>>;
}

/**
 * Signed fractional tolerance check shared by leaf evaluation descriptors. Mirrors
 * packages/app/src/helpers/productionValueCheck.ts semantics: percentDifference is signed
 * (negative below base, positive above), against a signed fractional bound (minPct/maxPct)
 * rather than a magnitude. Returns `incomplete` (not `fail`) when base or actual is
 * null/undefined, or when base is 0 and actual differs from it (division by zero is undefined).
 */
export function computeSignedToleranceVerdict(
  base: number | null | undefined,
  actual: number | null | undefined,
  bound: ToleranceBound,
): EvaluationRowResult {
  if (base === null || base === undefined || actual === null || actual === undefined) {
    return { verdict: 'incomplete' };
  }

  if (base === actual) {
    return {
      verdict: 'pass',
      percentDifference: 0,
      difference: 0,
      lowerBound: bound.minPct !== undefined ? base * (1 + bound.minPct) : undefined,
      upperBound: bound.maxPct !== undefined ? base * (1 + bound.maxPct) : undefined,
    };
  }

  if (base === 0) {
    // Percentage difference against a zero base is undefined.
    return { verdict: 'incomplete' };
  }

  const difference = actual - base;
  const percentDifference = difference / base;

  const lowerBound = bound.minPct !== undefined ? base * (1 + bound.minPct) : undefined;
  const upperBound = bound.maxPct !== undefined ? base * (1 + bound.maxPct) : undefined;

  const belowMin = bound.minPct !== undefined && percentDifference < bound.minPct;
  const aboveMax = bound.maxPct !== undefined && percentDifference > bound.maxPct;
  const verdict: EvaluationVerdict = belowMin || aboveMax ? 'fail' : 'pass';

  return {
    verdict,
    percentDifference,
    difference,
    lowerBound,
    upperBound,
  };
}
